import React, { useEffect } from "react";
import { X, Printer } from "lucide-react";
import Logo from "../../Logo";
import { refNo } from "../../../software/format";
import { printPosReceipt, companyRow, scriptText } from "../reports/legacy/docs";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function Receipt({ sale, branding, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  if (!sale) return null;
  const b = branding || {};
  const co = companyRow(b.company);
  const addr = [co.Address, co.Suburb, co.State, co.PostCode].filter(Boolean).join(", ");
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4 print:p-0 print:bg-white" data-testid="receipt-modal">
      <style>{`@media print { body * { visibility: hidden !important; } #receipt-print, #receipt-print * { visibility: visible !important; } #receipt-print { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; } .no-print { display: none !important; } }`}</style>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[92vh] overflow-y-auto print:shadow-none print:max-w-none print:max-h-none">
        <div className="flex justify-between items-center p-3 border-b border-slate-100 no-print">
          <span className="font-display font-bold text-slate-900 text-sm">Receipt {refNo("invoice", sale.sale_number)}</span>
          <div className="flex gap-2">
            <button onClick={() => printPosReceipt(sale, b.company)} data-testid="print-receipt-btn" className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-1.5"><Printer className="h-3.5 w-3.5" /> Print</button>
            <button onClick={onClose} data-testid="pos-receipt-close" className="rounded-full border border-slate-200 p-1.5 hover:bg-slate-50"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div id="receipt-print" className="receipt-doc p-6 font-mono2 text-[13px] text-slate-800">
          <div className="text-center mb-3">
            {co.logo_url ? <div className="flex justify-center mb-1"><img src={co.logo_url} alt="" className="max-h-16 w-auto object-contain" /></div> : <div className="flex justify-center mb-1"><Logo showText={false} /></div>}
            <p className="font-display font-bold text-base text-slate-900" data-testid="receipt-company">{co.CompanyID || b.company || "Aqualux Nexus"}</p>
            {(addr || b.address) && <p className="text-[11px] text-slate-500" data-testid="receipt-address">{addr || b.address}</p>}
            {(co.ABN || co.Phone || b.phone) && <p className="text-[11px] text-slate-500">{co.ABN ? `ABN: ${co.ABN}  ` : ""}{co.Phone ? `Phone: ${co.Phone}` : b.phone}</p>}
          </div>
          <div className="border-t border-dashed border-slate-300 my-2" />
          <div className="flex justify-between text-[11px] text-slate-500">
            <span>Receipt {refNo("invoice", sale.sale_number)}</span>
            <span>{new Date(sale.created_at).toLocaleString()}</span>
          </div>
          {sale.client_name && <p className="text-[11px] text-slate-500">Client: {sale.client_name}</p>}
          {sale.addressed_to && <p className="text-[11px] text-slate-500">Attn: {sale.addressed_to}{sale.addressed_relationship ? ` (${sale.addressed_relationship})` : ""}</p>}
          <p className="text-[11px] text-slate-500">Served by: {sale.clerk || sale.employee || "—"}</p>
          <div className="border-t border-dashed border-slate-300 my-2" />
          {(sale.items || []).map((ln, i) => (
            <div key={i} className="flex justify-between py-0.5">
              <span className="pr-2">{ln.qty} × {ln.name}{ln.discount ? ` (-${money(ln.discount)})` : ""}</span>
              <span>{money(ln.line_total)}</span>
            </div>
          ))}
          <div className="border-t border-dashed border-slate-300 my-2" />
          <div className="flex justify-between"><span>Subtotal</span><span>{money(sale.subtotal)}</span></div>
          {sale.discount > 0 && <div className="flex justify-between"><span>Discount</span><span>-{money(sale.discount)}</span></div>}
          <div className="flex justify-between font-bold text-slate-900 text-[15px] mt-1"><span>TOTAL</span><span>{money(sale.total)}</span></div>
          <div className="flex justify-between text-[11px] text-slate-500"><span>incl. GST</span><span>{money(sale.gst_total)}</span></div>
          <div className="border-t border-dashed border-slate-300 my-2" />
          {(sale.payments || []).map((p, i) => (
            <div key={i} className="flex justify-between text-[12px]"><span className="capitalize">{p.method}</span><span>{money(p.amount)}</span></div>
          ))}
          {sale.change > 0 && <div className="flex justify-between text-[12px]"><span>Change</span><span>{money(sale.change)}</span></div>}
          <p className="text-center text-[11px] text-slate-400 mt-3" data-testid="receipt-thanks">{scriptText("POS Receipt Thank You", { Company: co.CompanyID || b.company }) || "Thank you for your business"}</p>
        </div>
      </div>
    </div>
  );
}
